import React, { useState, useEffect, useRef } from 'react';
import evidenceAPI from '../services/evidenceAPI';
import {
  ClipboardList, Sparkles, Loader2, ChevronDown, ChevronUp, FileText, Search,
  CheckCircle, AlertCircle, Upload, X, Download, Edit3, Save, RefreshCw,
  GitCompare, Plus, Trash2, ArrowLeft
} from 'lucide-react';
import AdminSecurityHelpButton from '../components/Admin/AdminSecurityHelpButton';

type QAnswer = {
  id: number;
  question: string;
  answer: string;
  confidence: number;
  sources: string[];
  level: 'HIGH' | 'MEDIUM' | 'LOW';
  needs_review: boolean;
};

type QResult = {
  answers: QAnswer[];
  summary: { total: number; high_confidence: number; medium_confidence: number; needs_review: number; avg_confidence: number };
  evidence_used: number;
  ai_powered?: boolean;
};

type TabType = 'fill' | 'compare';

type CompareResult = {
  summary: {
    old_count: number; new_count: number; added: number; removed: number;
    modified: number; unchanged: number; change_percentage: number;
  };
  changes: {
    added: { index: number; question: string }[];
    removed: { index: number; question: string }[];
    modified: { index: number; old_question: string; new_question: string; similarity: number }[];
    unchanged: { index: number; question: string }[];
  };
};

const SAMPLE_QUESTIONS = `Does your organization enforce multi-factor authentication for all user accounts?
How is sensitive data protected both in transit and at rest?
What is your vulnerability management and patching cadence?
What is your incident response SLA for critical security events?
Do you conduct regular security awareness training for all employees?
Do you maintain cyber liability insurance?
Describe your business continuity plan and RTO/RPO targets.`;

const ACCEPTED_TYPES = '.pdf,.xlsx,.xls,.docx,.doc,.csv,.txt';

const DropZone: React.FC<{
  label: string;
  inputRef: React.RefObject<HTMLInputElement>;
  onFile: (e: React.ChangeEvent<HTMLInputElement>) => void;
  parsing: boolean;
  small?: boolean;
}> = ({ label, inputRef, onFile, parsing, small }) => (
  <div>
    <input ref={inputRef} type="file" accept={ACCEPTED_TYPES} className="hidden" onChange={onFile} />
    <button
      type="button"
      onClick={() => inputRef.current?.click()}
      disabled={parsing}
      className={`w-full flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-xl text-gray-500
        hover:border-purple-400 hover:text-purple-600 hover:bg-purple-50 transition-colors disabled:opacity-50
        ${small ? 'px-3 py-2 text-xs' : 'px-4 py-2.5 text-sm'}`}
    >
      {parsing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
      {parsing ? 'Parsing...' : label}
    </button>
  </div>
);

const QuestionnaireAutofill: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('fill');

  // ── Fill tab state ──────────────────────────────────────────────────────
  const [questionText, setQuestionText] = useState('');
  const [loading, setLoading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<QResult | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [evidenceCount, setEvidenceCount] = useState(0);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editedAnswers, setEditedAnswers] = useState<Record<number, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const autofillInProgressRef = useRef(false); // synchronous guard — state updates are async

  // ── Compare tab state ───────────────────────────────────────────────────
  const [oldText, setOldText] = useState('');
  const [newText, setNewText] = useState('');
  const [compareResult, setCompareResult] = useState<CompareResult | null>(null);
  const [comparing, setComparing] = useState(false);
  const [compareError, setCompareError] = useState<string | null>(null);
  const [compareTab, setCompareTab] = useState<'added' | 'removed' | 'modified' | 'unchanged'>('added');
  const oldFileRef = useRef<HTMLInputElement>(null);
  const newFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    evidenceAPI.getEvidenceItems().then(items => setEvidenceCount(items.length)).catch(() => {});
  }, []);

  // ── File upload → backend parse ─────────────────────────────────────────
  const handleFileUpload = async (file: File, target: 'fill' | 'old' | 'new') => {
    if (!file) return;
    setParsing(true);
    console.log(`[QuestionnaireAutofill] 📂 Step 1: File selected — name="${file.name}" size=${file.size} bytes type="${file.type}"`);
    try {
      console.log(`[QuestionnaireAutofill] 🔄 Step 2: Sending to backend parser...`);
      const data = await evidenceAPI.parseQuestionnaireFile(file);
      console.log(`[QuestionnaireAutofill] ✅ Step 3: Parser response — success=${data.success} questions=${data.questions?.length ?? 0} source="${data.source_file}"`);
      const meta = data.parse_meta;
      if (meta) {
        console.log(`[QuestionnaireAutofill]    Mode: ${meta.mode?.toUpperCase()} | Raw lines: ${meta.raw_count} → cleaned: ${data.total}`);
        if (meta.sheets?.length) {
          console.log(`[QuestionnaireAutofill]    Sheets (${meta.sheets.length}):`);
          meta.sheets.forEach((s: any) => {
            if (s.skipped) {
              console.log(`[QuestionnaireAutofill]      • "${s.name}" (${s.rows} rows) — skipped (no question column)`);
            } else {
              console.log(`[QuestionnaireAutofill]      • "${s.name}" (${s.rows} rows) — extracted ${s.extracted} questions via column "${s.column}"`);
            }
          });
          if (meta.column_used) console.log(`[QuestionnaireAutofill]    Column used: "${meta.column_used}"`);
        }
      }
      if (data.questions?.length > 0) {
        console.log(`[QuestionnaireAutofill]    First Q: "${data.questions[0].substring(0, 80)}"`);
        console.log(`[QuestionnaireAutofill]    Last  Q: "${data.questions[data.questions.length - 1].substring(0, 80)}"`);
      }
      if (data.success && data.questions.length > 0) {
        const text = data.questions.join('\n');
        if (target === 'fill') {
          setQuestionText(text);
          setUploadedFile(file);
        } else if (target === 'old') {
          setOldText(text);
        } else {
          setNewText(text);
        }
      } else {
        console.warn(`[QuestionnaireAutofill] ⚠️  No questions found in file`);
        setError(`No questions found in ${file.name}. Try pasting manually.`);
      }
    } catch (err: any) {
      console.error(`[QuestionnaireAutofill] ❌ Step 3 error: ${err?.response?.data?.message || err?.message}`);
      // Fallback: client-side text parse for CSV/TXT
      if (file.name.endsWith('.csv') || file.name.endsWith('.txt')) {
        console.log(`[QuestionnaireAutofill] 🔄 Falling back to client-side parse for ${file.name}`);
        const reader = new FileReader();
        reader.onload = (ev) => {
          const text = (ev.target?.result as string) || '';
          const lines = text.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 10).join('\n');
          console.log(`[QuestionnaireAutofill] ✅ Client-side parse done — ${lines.split('\n').length} lines`);
          if (target === 'fill') { setQuestionText(lines); setUploadedFile(file); }
          else if (target === 'old') setOldText(lines);
          else setNewText(lines);
        };
        reader.readAsText(file);
      } else {
        setError(`Could not parse ${file.name}: ${err.response?.data?.message || err.message}`);
      }
    } finally {
      setParsing(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, target: 'fill' | 'old' | 'new') => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file, target);
    e.target.value = '';
  };

  // ── Auto-fill ───────────────────────────────────────────────────────────
  const handleAutofill = async () => {
    if (autofillInProgressRef.current) return; // synchronous guard — prevents duplicates even within same render cycle
    autofillInProgressRef.current = true;
    const questions = questionText.trim().split('\n').map(q => q.trim()).filter(Boolean);
    if (questions.length === 0) { autofillInProgressRef.current = false; return; }
    setLoading(true);
    setResult(null);
    setError(null);
    setProgress(0);
    setEditedAnswers({});

    console.log(`[QuestionnaireAutofill] ▶️  Step 4: Auto-fill started — ${questions.length} questions`);
    console.log(`[QuestionnaireAutofill]    Evidence items available: ${evidenceCount}`);
    console.log(`[QuestionnaireAutofill]    Q[0]: "${questions[0]?.substring(0, 80)}"`);
    if (questions.length > 1) console.log(`[QuestionnaireAutofill]    Q[last]: "${questions[questions.length - 1]?.substring(0, 80)}"`);

    // Slow crawl: ~1.3% every 400ms → reaches ~95% in ~30s, never freezes visibly
    const prog = setInterval(() => setProgress(p => Math.min(p + 1.3, 95)), 400);
    const t0 = Date.now();
    let isDuplicate = false;
    try {
      console.log(`[QuestionnaireAutofill] 🤖 Step 5: Calling backend AI autofill API...`);
      const data = await evidenceAPI.aiQuestionnaireAutofill(questions);
      clearInterval(prog);
      setProgress(100);
      const elapsed = Date.now() - t0;
      console.log(`[QuestionnaireAutofill] ✅ Step 6: Response received in ${elapsed}ms`);
      console.log(`[QuestionnaireAutofill]    Total answers: ${data?.answers?.length}`);
      console.log(`[QuestionnaireAutofill]    AI powered: ${data?.ai_powered}`);
      console.log(`[QuestionnaireAutofill]    Summary:`, data?.summary);
      setError(null);
      setResult(data);
      console.log(`[QuestionnaireAutofill]    setResult called ✅`);
    } catch (err: any) {
      clearInterval(prog);
      // 429 = server-side duplicate guard fired — the primary request is still running, discard silently
      if (err?.response?.status === 429) {
        isDuplicate = true;
        console.warn(`[QuestionnaireAutofill] ⚠️  Duplicate blocked (429) — primary request still in progress, ignoring`);
        return;
      }
      setProgress(0);
      const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Auto-fill failed. Please try again.';
      console.error(`[QuestionnaireAutofill] ❌ Step 6 failed after ${Date.now() - t0}ms: ${msg}`);
      console.error(`[QuestionnaireAutofill]    HTTP status: ${err?.response?.status}`);
      console.error(`[QuestionnaireAutofill]    Full error:`, err?.response?.data);
      setError(msg);
    } finally {
      // Don't touch loading/ref if this was a silently-discarded duplicate — the primary call owns those
      if (!isDuplicate) {
        autofillInProgressRef.current = false;
        setLoading(false);
      }
    }
  };

  // ── Edit answer inline ──────────────────────────────────────────────────
  const startEdit = (id: number, currentAnswer: string) => {
    setEditingId(id);
    setEditedAnswers(prev => ({ ...prev, [id]: currentAnswer }));
  };

  const saveEdit = (id: number) => {
    if (!result) return;
    setResult({
      ...result,
      answers: result.answers.map(a =>
        a.id === id ? { ...a, answer: editedAnswers[id] ?? a.answer } : a
      )
    });
    setEditingId(null);
  };

  // ── Export ──────────────────────────────────────────────────────────────
  const handleExport = async (format: 'excel' | 'csv' | 'pdf' | 'txt') => {
    if (!result) return;
    const answers = result.answers.map(a => ({
      ...a,
      answer: editedAnswers[a.id] ?? a.answer
    }));

    try {
      const blob = await evidenceAPI.exportQuestionnaireAnswers(format, answers, 'Security Questionnaire Responses');
      const extMap = { excel: 'xlsx', csv: 'csv', pdf: 'pdf', txt: 'txt' };
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `questionnaire-answers-${new Date().toISOString().split('T')[0]}.${extMap[format]}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      // Fallback: txt export client-side
      const lines = answers.map(a =>
        `Q: ${a.question}\nA: ${a.answer}\nConfidence: ${a.confidence}% (${a.level})\nSources: ${a.sources.join(', ') || 'N/A'}\n`
      );
      const content = `Questionnaire Answers\n${'='.repeat(50)}\n\n` + lines.join('\n---\n');
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `questionnaire-answers.txt`; a.click();
      URL.revokeObjectURL(url);
    }
  };

  // ── Compare ─────────────────────────────────────────────────────────────
  const handleCompare = async () => {
    const oldQ = oldText.split('\n').map(q => q.trim()).filter(Boolean);
    const newQ = newText.split('\n').map(q => q.trim()).filter(Boolean);
    if (oldQ.length === 0 || newQ.length === 0) {
      setCompareError('Both old and new questionnaire questions are required.');
      return;
    }
    setComparing(true);
    setCompareResult(null);
    setCompareError(null);
    try {
      const data = await evidenceAPI.compareQuestionnaires(oldQ, newQ);
      setCompareResult(data);
      // Auto-select the most interesting tab
      if (data.changes.added.length > 0) setCompareTab('added');
      else if (data.changes.modified.length > 0) setCompareTab('modified');
      else if (data.changes.removed.length > 0) setCompareTab('removed');
      else setCompareTab('unchanged');
    } catch (err: any) {
      setCompareError(err.response?.data?.message || 'Comparison failed.');
    } finally {
      setComparing(false);
    }
  };

  // ── Helpers ─────────────────────────────────────────────────────────────

  const levelColor = (level: string) => ({
    HIGH: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200', badge: 'bg-green-100 text-green-700' },
    MEDIUM: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700' },
    LOW: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', badge: 'bg-red-100 text-red-700' },
  }[level] || { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200', badge: 'bg-gray-100 text-gray-700' });

  // ════════════════════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-5 flex items-center gap-3">
        <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
          <ClipboardList className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Questionnaire Auto-Fill</h1>
          <p className="text-sm text-gray-500">AI answers security questionnaires from your collected evidence</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="flex items-center gap-2 bg-purple-50 border border-purple-100 rounded-lg px-3 py-1.5">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-xs text-purple-700 font-medium">{evidenceCount} evidence items</span>
          </div>
          <AdminSecurityHelpButton screenId="questionnaire-autofill" />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-5 w-fit">
        <button
          onClick={() => setActiveTab('fill')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'fill' ? 'bg-white shadow-sm text-purple-700' : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <Sparkles className="w-4 h-4" /> AI Auto-Fill
        </button>
        <button
          onClick={() => setActiveTab('compare')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'compare' ? 'bg-white shadow-sm text-purple-700' : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <GitCompare className="w-4 h-4" /> Version Compare
        </button>
      </div>

      {/* ── FILL TAB ─────────────────────────────────────────────────────── */}
      {activeTab === 'fill' && (
        <>
          {!result ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input panel */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Paste questions <span className="text-gray-400 font-normal">(one per line)</span>
                </label>
                <textarea
                  value={questionText}
                  onChange={e => setQuestionText(e.target.value)}
                  rows={10}
                  placeholder={SAMPLE_QUESTIONS}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none font-mono"
                />

                {/* File upload */}
                <div className="mt-3">
                  {uploadedFile ? (
                    <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg text-sm">
                      <FileText className="w-4 h-4 text-purple-500 flex-shrink-0" />
                      <span className="flex-1 text-purple-700 truncate">{uploadedFile.name}</span>
                      <button onClick={() => { setUploadedFile(null); setQuestionText(''); }} className="text-purple-400 hover:text-purple-700">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <DropZone
                      label="Upload file (PDF, Excel, Word, CSV, TXT)"
                      inputRef={fileInputRef}
                      onFile={e => { const f = e.target.files?.[0]; if (f) handleFileUpload(f, 'fill'); e.target.value = ''; }}
                      parsing={parsing}
                    />
                  )}
                </div>

                {/* Progress bar */}
                {loading && (
                  <div className="mt-3 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-purple-500 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
                  </div>
                )}

                {error && (
                  <div className="mt-3 flex items-start gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-red-700">{error}</p>
                  </div>
                )}

                <div className="flex items-center gap-3 mt-3">
                  <button
                    onClick={handleAutofill}
                    disabled={loading || !questionText.trim()}
                    className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:opacity-40 transition-colors font-medium text-sm"
                  >
                    {loading
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing {questionText.split('\n').filter(Boolean).length} questions ({Math.round(progress)}%)...</>
                      : <><Sparkles className="w-4 h-4" /> Auto-Fill with AI</>}
                  </button>
                  <button
                    onClick={() => setQuestionText(SAMPLE_QUESTIONS)}
                    className="px-4 py-3 border border-gray-300 text-gray-600 rounded-xl hover:bg-gray-50 text-sm transition-colors"
                  >
                    Sample
                  </button>
                </div>
              </div>

              {/* How it works */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">How It Works</p>
                <div className="space-y-3">
                  {[
                    { icon: Upload, text: 'Upload PDF, Excel, Word, CSV, or TXT — questions are auto-extracted from any format' },
                    { icon: Search, text: `AI searches across your ${evidenceCount} collected evidence items (GitHub, AWS, SSL, Audit Logs)` },
                    { icon: CheckCircle, text: 'Each answer gets a confidence score (HIGH / MEDIUM / LOW) based on supporting evidence' },
                    { icon: Edit3, text: 'Edit any answer inline before exporting — your changes are preserved in the export' },
                    { icon: Download, text: 'Export back to Excel, CSV, PDF, or TXT — keeping original question structure' },
                  ].map(({ icon: Icon, text }, i) => (
                    <div key={i} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                      <Icon className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-gray-600">{text}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-5 p-4 bg-purple-50 rounded-lg border border-purple-100">
                  <p className="text-xs font-semibold text-purple-700 mb-2">Confidence Levels</p>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-xs"><div className="w-3 h-3 rounded-full bg-green-500" /><span className="text-gray-700">HIGH (80%+) — Strong evidence found, ready to submit</span></div>
                    <div className="flex items-center gap-2 text-xs"><div className="w-3 h-3 rounded-full bg-amber-500" /><span className="text-gray-700">MEDIUM (50–79%) — Evidence found, review recommended</span></div>
                    <div className="flex items-center gap-2 text-xs"><div className="w-3 h-3 rounded-full bg-red-400" /><span className="text-gray-700">LOW (&lt;50%) — Needs human review before submission</span></div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div>
              {/* Summary cards */}
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-5">
                {[
                  { value: result.summary.total, label: 'Questions', sub: `${result.evidence_used} evidence items`, color: 'gray' },
                  { value: result.summary.high_confidence, label: 'High Confidence', sub: 'Ready to export', color: 'green' },
                  { value: result.summary.medium_confidence, label: 'Medium', sub: 'Consider review', color: 'amber' },
                  { value: result.summary.needs_review, label: 'Needs Review', sub: 'Human input needed', color: 'red' },
                  { value: `${result.summary.avg_confidence}%`, label: 'Avg Confidence', sub: result.ai_powered ? 'AI powered' : 'Keyword match', color: 'purple' },
                ].map(({ value, label, sub, color }, i) => (
                  <div key={i} className={`bg-${color}-50 border border-${color}-200 rounded-xl p-4 text-center shadow-sm`}>
                    <p className={`text-3xl font-bold text-${color}-700`}>{value}</p>
                    <p className={`text-xs text-${color}-600 mt-1`}>{label}</p>
                    <p className={`text-xs text-${color}-500 mt-0.5`}>{sub}</p>
                  </div>
                ))}
              </div>

              {/* Action bar */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-5 flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-4 flex-1 flex-wrap">
                  <span className="text-sm text-gray-600">
                    <span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block mr-1.5" />
                    <strong>{result.summary.high_confidence}</strong> High
                  </span>
                  <span className="text-sm text-gray-600">
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-500 inline-block mr-1.5" />
                    <strong>{result.summary.medium_confidence}</strong> Medium
                  </span>
                  <span className="text-sm text-gray-600">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-400 inline-block mr-1.5" />
                    <strong>{result.summary.needs_review}</strong> Review
                  </span>
                  {result.ai_powered && (
                    <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full font-medium">AI Powered</span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Export buttons */}
                  {(['excel', 'csv', 'pdf'] as const).map(fmt => (
                    <button
                      key={fmt}
                      onClick={() => handleExport(fmt)}
                      className="text-xs px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 transition-colors flex items-center gap-1.5 capitalize"
                    >
                      <Download className="w-3.5 h-3.5" /> {fmt.toUpperCase()}
                    </button>
                  ))}
                  <button
                    onClick={() => { setResult(null); setQuestionText(''); setUploadedFile(null); }}
                    className="text-sm px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600 transition-colors flex items-center gap-1.5"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" /> New
                  </button>
                </div>
              </div>

              {/* Answer cards */}
              <div className="space-y-2">
                {result.answers.map(ans => {
                  const c = levelColor(ans.level);
                  const isEditing = editingId === ans.id;
                  const currentAnswer = editedAnswers[ans.id] ?? ans.answer;
                  return (
                    <div key={ans.id} className={`bg-white border ${c.border} rounded-xl overflow-hidden`}>
                      <div
                        role="button"
                        tabIndex={0}
                        className="w-full flex items-start gap-4 p-4 text-left hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => !isEditing && setExpanded(expanded === ans.id ? null : ans.id)}
                        onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && !isEditing) setExpanded(expanded === ans.id ? null : ans.id); }}
                      >
                        {/* Confidence badge */}
                        <div className={`flex-shrink-0 w-14 h-14 rounded-xl flex flex-col items-center justify-center ${c.bg}`}>
                          <span className={`text-xl font-bold ${c.text}`}>{ans.confidence}</span>
                          <span className={`text-[10px] ${c.text}`}>%</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${c.badge}`}>{ans.level}</span>
                            {ans.needs_review && <span className="text-xs text-red-600 font-medium">⚠ Needs Review</span>}
                            {editedAnswers[ans.id] && (
                              <span className="text-xs text-blue-600 font-medium">Edited</span>
                            )}
                          </div>
                          <p className="text-sm font-medium text-gray-800">{ans.question}</p>
                          {expanded !== ans.id && !isEditing && (
                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{currentAnswer}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={e => { e.stopPropagation(); if (isEditing) saveEdit(ans.id); else { startEdit(ans.id, ans.answer); setExpanded(ans.id); } }}
                            className={`p-1.5 rounded-lg transition-colors ${isEditing ? 'bg-green-100 text-green-600 hover:bg-green-200' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'}`}
                            title={isEditing ? 'Save' : 'Edit answer'}
                          >
                            {isEditing ? <Save className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
                          </button>
                          {!isEditing && (
                            expanded === ans.id
                              ? <ChevronUp className="w-4 h-4 text-gray-400" />
                              : <ChevronDown className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                      </div>

                      {(expanded === ans.id || isEditing) && (
                        <div className="px-5 pb-5 pt-2 border-t border-gray-100 bg-gray-50">
                          {isEditing ? (
                            <div>
                              <textarea
                                value={editedAnswers[ans.id] ?? ans.answer}
                                onChange={e => setEditedAnswers(prev => ({ ...prev, [ans.id]: e.target.value }))}
                                rows={5}
                                className="w-full text-sm border border-blue-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                autoFocus
                              />
                              <div className="flex gap-2 mt-2">
                                <button
                                  onClick={() => saveEdit(ans.id)}
                                  className="px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 flex items-center gap-1"
                                >
                                  <Save className="w-3 h-3" /> Save
                                </button>
                                <button
                                  onClick={() => setEditingId(null)}
                                  className="px-3 py-1.5 border border-gray-300 text-gray-600 text-xs rounded-lg hover:bg-gray-100"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <p className="text-sm text-gray-800 leading-relaxed">{currentAnswer}</p>
                              {ans.sources.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-1.5">
                                  <span className="text-xs text-gray-500 mr-1">Sources:</span>
                                  {ans.sources.map((s, si) => (
                                    <span key={si} className="text-xs px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-full">{s}</span>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── COMPARE TAB ──────────────────────────────────────────────────── */}
      {activeTab === 'compare' && (
        <div>
          {!compareResult ? (
            <div className="space-y-5">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
                <strong>Questionnaire Version Compare</strong> — Paste or upload two versions of a questionnaire to see what changed: new questions added, old ones removed, and questions that were reworded.
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Old version */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-red-600">V1</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-700">Previous Version</span>
                  </div>
                  <textarea
                    value={oldText}
                    onChange={e => setOldText(e.target.value)}
                    rows={10}
                    placeholder="Paste old questionnaire questions (one per line)..."
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none font-mono"
                  />
                  <div className="mt-2">
                    <input ref={oldFileRef} type="file" accept={ACCEPTED_TYPES} className="hidden"
                      onChange={e => handleFileChange(e, 'old')} />
                    <DropZone label="Upload old version" inputRef={oldFileRef}
                      onFile={e => handleFileChange(e, 'old')} parsing={parsing} small />
                  </div>
                  {oldText && (
                    <p className="text-xs text-gray-500 mt-2">
                      {oldText.split('\n').filter(Boolean).length} questions
                    </p>
                  )}
                </div>

                {/* New version */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-green-600">V2</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-700">New Version</span>
                  </div>
                  <textarea
                    value={newText}
                    onChange={e => setNewText(e.target.value)}
                    rows={10}
                    placeholder="Paste new questionnaire questions (one per line)..."
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 resize-none font-mono"
                  />
                  <div className="mt-2">
                    <input ref={newFileRef} type="file" accept={ACCEPTED_TYPES} className="hidden"
                      onChange={e => handleFileChange(e, 'new')} />
                    <DropZone label="Upload new version" inputRef={newFileRef}
                      onFile={e => handleFileChange(e, 'new')} parsing={parsing} small />
                  </div>
                  {newText && (
                    <p className="text-xs text-gray-500 mt-2">
                      {newText.split('\n').filter(Boolean).length} questions
                    </p>
                  )}
                </div>
              </div>

              {compareError && (
                <div className="flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{compareError}</p>
                </div>
              )}

              <button
                onClick={handleCompare}
                disabled={comparing || !oldText.trim() || !newText.trim()}
                className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:opacity-40 transition-colors font-medium"
              >
                {comparing ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitCompare className="w-4 h-4" />}
                {comparing ? 'Comparing...' : 'Compare Versions'}
              </button>
            </div>
          ) : (
            <div>
              {/* Compare summary cards */}
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-5">
                {[
                  { value: compareResult.summary.added, label: 'Added', color: 'green', icon: Plus },
                  { value: compareResult.summary.removed, label: 'Removed', color: 'red', icon: Trash2 },
                  { value: compareResult.summary.modified, label: 'Modified', color: 'amber', icon: Edit3 },
                  { value: compareResult.summary.unchanged, label: 'Unchanged', color: 'gray', icon: CheckCircle },
                  { value: `${compareResult.summary.change_percentage}%`, label: 'Changed', color: 'purple', icon: RefreshCw },
                ].map(({ value, label, color, icon: Icon }, i) => (
                  <div key={i} className={`bg-${color}-50 border border-${color}-200 rounded-xl p-4 text-center shadow-sm`}>
                    <Icon className={`w-5 h-5 text-${color}-500 mx-auto mb-1`} />
                    <p className={`text-2xl font-bold text-${color}-700`}>{value}</p>
                    <p className={`text-xs text-${color}-600 mt-0.5`}>{label}</p>
                  </div>
                ))}
              </div>

              {/* Compare summary bar */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-5 flex items-center gap-4 flex-wrap">
                <span className="text-sm text-gray-600">V1: <strong>{compareResult.summary.old_count}</strong> questions</span>
                <span className="text-gray-300">→</span>
                <span className="text-sm text-gray-600">V2: <strong>{compareResult.summary.new_count}</strong> questions</span>
                <div className="ml-auto flex gap-2">
                  <button
                    onClick={() => setCompareResult(null)}
                    className="text-sm px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600 flex items-center gap-1.5"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" /> Compare Again
                  </button>
                  {compareResult.changes.added.length > 0 && (
                    <button
                      onClick={() => {
                        setActiveTab('fill');
                        setQuestionText(compareResult.changes.added.map(a => a.question).join('\n'));
                        setResult(null);
                      }}
                      className="text-sm px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-1.5"
                    >
                      <Sparkles className="w-3.5 h-3.5" /> Auto-Fill New Questions
                    </button>
                  )}
                </div>
              </div>

              {/* Change detail tabs */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="flex border-b border-gray-200">
                  {[
                    { key: 'added', label: `Added (${compareResult.changes.added.length})`, color: 'green' },
                    { key: 'removed', label: `Removed (${compareResult.changes.removed.length})`, color: 'red' },
                    { key: 'modified', label: `Modified (${compareResult.changes.modified.length})`, color: 'amber' },
                    { key: 'unchanged', label: `Unchanged (${compareResult.changes.unchanged.length})`, color: 'gray' },
                  ].map(({ key, label, color }) => (
                    <button
                      key={key}
                      onClick={() => setCompareTab(key as typeof compareTab)}
                      className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                        compareTab === key
                          ? `border-b-2 border-${color}-500 text-${color}-700 bg-${color}-50`
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                <div className="p-4 space-y-2 max-h-[500px] overflow-y-auto">
                  {compareTab === 'added' && (
                    compareResult.changes.added.length === 0
                      ? <p className="text-sm text-gray-500 text-center py-6">No new questions added</p>
                      : compareResult.changes.added.map((item, i) => (
                        <div key={i} className="flex gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <Plus className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <span className="text-xs text-green-600 font-medium">Q{item.index} — New</span>
                            <p className="text-sm text-gray-800 mt-0.5">{item.question}</p>
                          </div>
                        </div>
                      ))
                  )}
                  {compareTab === 'removed' && (
                    compareResult.changes.removed.length === 0
                      ? <p className="text-sm text-gray-500 text-center py-6">No questions removed</p>
                      : compareResult.changes.removed.map((item, i) => (
                        <div key={i} className="flex gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <Trash2 className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <span className="text-xs text-red-600 font-medium">Q{item.index} — Removed</span>
                            <p className="text-sm text-gray-500 line-through mt-0.5">{item.question}</p>
                          </div>
                        </div>
                      ))
                  )}
                  {compareTab === 'modified' && (
                    compareResult.changes.modified.length === 0
                      ? <p className="text-sm text-gray-500 text-center py-6">No questions were reworded</p>
                      : compareResult.changes.modified.map((item, i) => (
                        <div key={i} className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Edit3 className="w-4 h-4 text-amber-600" />
                            <span className="text-xs text-amber-700 font-medium">Q{item.index} — Modified ({item.similarity}% similar)</span>
                          </div>
                          <p className="text-xs text-gray-500 line-through mb-1">{item.old_question}</p>
                          <p className="text-sm text-gray-800">{item.new_question}</p>
                        </div>
                      ))
                  )}
                  {compareTab === 'unchanged' && (
                    compareResult.changes.unchanged.length === 0
                      ? <p className="text-sm text-gray-500 text-center py-6">No unchanged questions</p>
                      : compareResult.changes.unchanged.map((item, i) => (
                        <div key={i} className="flex gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <CheckCircle className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-gray-600">{item.question}</p>
                        </div>
                      ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuestionnaireAutofill;
